import React, { useEffect, useState } from 'react';
import { Link2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { OfficialAccommodationLookup } from './OfficialAccommodationLookup';
import { useChangePreRegistrationStatus } from '../hooks/usePreRegisteredAccommodationMutations';
import { PRE_REGISTRATION_STATUS_IDS } from '../utils/status-transitions.utils';
import type { MunicipalListAccommodation } from '../types/pre-registration.types';
import type { LinkedOfficialAccommodation } from '../types/official-accommodation.types';

// Statuses that require the official accommodation
export type LinkStatusTarget = 'already_registered' | 'regularized';

const TITLES: Record<LinkStatusTarget, string> = {
  already_registered: 'Marcar como Ya registrado',
  regularized: 'Marcar como Regularizado',
};

interface LinkOfficialAccommodationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row: MunicipalListAccommodation;
  target: LinkStatusTarget;
  departmentId?: number;
}

// Picks the official REPAT accommodation the row corresponds to and changes its status
export const LinkOfficialAccommodationDialog: React.FC<LinkOfficialAccommodationDialogProps> = ({
  open,
  onOpenChange,
  row,
  target,
  departmentId,
}) => {
  const [query, setQuery] = useState(row.establishmentName);
  const [selected, setSelected] = useState<LinkedOfficialAccommodation | null>(null);
  const changeStatus = useChangePreRegistrationStatus();

  // Each opening starts from the row's trade name
  useEffect(() => {
    if (open) {
      setQuery(row.establishmentName);
      setSelected(null);
    }
  }, [open, row.establishmentName]);

  const confirm = () => {
    if (!selected) return;
    changeStatus.mutate(
      {
        id: row.id,
        payload: { statusId: PRE_REGISTRATION_STATUS_IDS[target], accommodationId: selected.id },
      },
      { onSuccess: () => onOpenChange(false) }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{TITLES[target]}</DialogTitle>
          <DialogDescription>
            Elegí el alojamiento oficial de REPAT que corresponde a "{row.establishmentName}".
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1">
          <OfficialAccommodationLookup
            id="link-official-accommodation-search"
            query={query}
            onQueryChange={setQuery}
            departmentId={departmentId}
            selectedId={selected?.id}
            onSelect={({ id, fileNumber, establishmentName }) =>
              setSelected(id === selected?.id ? null : { id, fileNumber, establishmentName })
            }
            actionLabel="Seleccionar"
          />
        </div>

        <DialogFooter className="gap-2 sm:space-x-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={changeStatus.isPending}>
            Cancelar
          </Button>
          <Button onClick={confirm} disabled={!selected || changeStatus.isPending}>
            {changeStatus.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Link2 className="mr-2 h-4 w-4" />
            )}
            {selected ? `Vincular al legajo ${selected.fileNumber}` : 'Vincular'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
